import { BASE_URL, REPORT_EXPORT_URL } from "services/endpoints";
import dayjs from "utils/dayjs-config";
import { message } from "utils/ciq-message";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z
} from "utils/dateutils";
import { getTheAppUrl, renderTemplate } from "utils/utils";
import { ProjectSettingType } from "context/ProjectProvider";
import sanitizeFileName from "sanitize-filename";
import footerHtmlTemplate from "./templates/footer.html?raw";
import headerHtmlTemplate from "./templates/header.html?raw";
import { ErrorMessages } from "../../constants";
import { client } from "../axios";
import {
  // getAccessToken,
  getCurrentSubscriptionId,
  getIDToken,
  getRefreshToken
} from "../auth";
import { IJSReportData } from "./jsreport-request-data.interface";

/**
 * Method to sanitize the download file name and give the final file name to save
 * @param projectDetails
 * @returns
 */
export const getTheFileName = (projectName?: any) => {
  const cleanedProjectName = sanitizeFileName(
    projectName || "project-name-not-found"
  );
  // Project Timezone is set as default timezone for dayjs across the application in navbar module.
  const today = dayjs().format(DATE_FORMAT_MMDDYYYY);
  const fileName = `Risk Report of ${cleanedProjectName}_${today}`; //  Risk Report of (Project Name)_(Date of Export in MM/DD/YYYY)

  return fileName;
};

const getTemplateData = (templateData: any) => {
  return {
    "#{reportTitle}": templateData.reportTitle || "",
    // createdBy is always a template literal `${...} ${...}`, never falsy (min " "), so || "" cannot fire
    /* v8 ignore next */
    "#{createdBy}": templateData.createdBy || "",
    "#{createdOn}": templateData.createdOn,
    "#{projectName}": templateData.projectName || ""
  };
};

const getTheHeaderTemplate = (templateData: any) => {
  return renderTemplate(headerHtmlTemplate, getTemplateData(templateData));
};

const getTheFooterTemplate = (templateData: any) => {
  return renderTemplate(footerHtmlTemplate, getTemplateData(templateData));
};

const getJSReportServerConfig = (
  templateData: any,
  codeFetchResponse: any
): IJSReportData => {
  const { reportTemplateUrl } = templateData;

  let urlWithToken = renderTemplate(reportTemplateUrl, {
    "#{subscriptionId}": getCurrentSubscriptionId(),
    "#{code}": codeFetchResponse?.data?.success?.id
  });

  urlWithToken = getTheAppUrl() + urlWithToken;

  const data: IJSReportData = {
    template: {
      content: " ",
      engine: "none",
      recipe: "chrome-pdf",
      chrome: {
        format: "A4",
        landscape: true,
        waitForJS: true,
        waitForNetworkIdle: true,
        printBackground: false,
        numberOfWorkers: 3,

        marginLeft: "",
        marginTop: "2.4cm",
        marginBottom: "1.2cm",
        margin: {
          top: "2.4cm",
          bottom: "1.2cm",
          left: ""
        },

        displayHeaderFooter: true,
        headerTemplate: getTheHeaderTemplate(templateData),
        footerTemplate: getTheFooterTemplate(templateData),

        url: urlWithToken
      }
    }
  };

  return data;
};

/**
 * Method to download the report as pdf format
 * @param reportTemplateUrl
 * @param projectDetails
 * @returns
 */
export const DownloadReport = async (
  reportTemplateUrl: string,
  reportTitle: string,
  currentUser: any,
  projectDetails: ProjectSettingType | undefined,
  filename: string
) => {
  try {
    const templateData: any = {
      reportTemplateUrl,
      reportTitle,
      createdBy: `${currentUser.first_name || currentUser["first-name"]} ${
        currentUser.last_name || currentUser["last-name"]
      }`,
      createdOn: dayjs().format(DATE_FORMAT_MMDDYYYY_HHMM_A_Z),
      projectName: projectDetails?.name || ""
    };

    const codeFetchUrl = `${BASE_URL}/tmp_token`;
    const codeFetchResponse = await client.post(codeFetchUrl, {
      accessToken: getIDToken(),
      id_token: getIDToken(),
      ref_token: getRefreshToken()
    });

    const requestData: any = getJSReportServerConfig(
      templateData,
      codeFetchResponse
    );

    // const reportUrl = "http://localhost:4000/reports"; // for local development
    const reportUrl = `${BASE_URL}${REPORT_EXPORT_URL}`;

    const downloadResponse = await client.post(reportUrl, requestData, {
      responseType: "blob",
      headers: {
        "Content-Type": "application/json"
      }
    });

    const href = URL.createObjectURL(downloadResponse.data);

    // create "a" HTML element with href to file & click
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", filename); // give custom file name
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);

    message.success(ErrorMessages.reportExport.submittalSuccessMessage);
    return downloadResponse;
  } catch (error) {
    message.error(ErrorMessages.reportExport.errorMessage);
    return { error };
  }
};
